import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { loadStripe, Stripe, StripeCardNumberElement } from "@stripe/stripe-js";
import { axiosService } from "network/axios";
import { calculateDueDate } from "network/helper";
import { updateProjectStatus, saveDraftProjectId, clearDraftProjectId } from "store/customerProject/reducer";
import { uploadProjectSelector, draftProjectIdSelector } from "store/customerProject/selector";
import { getCardsRequest, removeCardInfoRequest } from "store/user/reducer";
import { cardsSelector, isGettingCardsSelector, userSelector } from "store/user/selector";
import { calculateOrderTotal } from "../constants/pricing";
import { lockDraftSaves, unlockDraftSaves, waitForPendingDraftSave } from "./checkoutPaymentGuard";

const getLS_STEP = (userId: string) => `order.step.${userId}`;
const getLS_MAX_STEP = (userId: string) => `order.maxStep.${userId}`;
const getLS_DRAFT = (userId: string) => `order.draft.${userId}`;
const getLS_DRAFT_PROJECT_ID = (userId: string) => `order.draftProjectId.${userId}`;

const FINALIZE_PAID_ORDER_ATTEMPTS = 3;
const FINALIZE_PAID_ORDER_RETRY_MS = 1500;

const formatOrderNumber = (id: string | number) => String(id).padStart(6, "0");

const ORDER_NOT_SAVED_MESSAGE =
  "We couldn't save your order details, so no payment was taken. Please try again or contact support.";

const paidButNotFinalizedMessage = (id?: string | number | null) =>
  `Your payment was received${id ? ` for order #${formatOrderNumber(id)}` : ""}, but we couldn't finalize the order. ` +
  "Please do not pay again — contact support and we will complete it for you.";

export function useStep4Payment() {
  const { orderId } = useParams();
  const uploadProject = useSelector(uploadProjectSelector as any) as any;
  const user = useSelector(userSelector as any) as any;
  const cards = useSelector(cardsSelector as any) as any;
  const isGettingCards = useSelector(isGettingCardsSelector as any) as boolean;
  const draftProjectId = useSelector(draftProjectIdSelector as any) as string | null;

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const isRedeemEnabled = localStorage.getItem("redeem") ? true : false;

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [payment, setPayment] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [openCardInputForm, setOpenCardInputForm] = useState(false);

  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [cardNumberElement, setCardNumberElement] = useState<StripeCardNumberElement | null>(null);
  const [cardHolderName, setCardHolderName] = useState("");
  const [cardBrand, setCardBrand] = useState<string>("");
  const [isSavingNewCard, setIsSavingNewCard] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);

  const paypalRef = useRef<{ open: () => void } | null>(null);

  const total = useMemo(() => {
    const calculated = calculateOrderTotal({
      transitionType: uploadProject.transitionType,
      include_expedited_turnaround: (uploadProject as any).include_expedited_turnaround,
      totalPage: uploadProject.totalPage,
      totalWords: uploadProject.totalWords,
      targetLanguage: uploadProject.targetLanguage || [],
      isNotarized: uploadProject.isNotarized,
      isMailHardCopy: uploadProject.isMailHardCopy,
      include_desktop_publishing: uploadProject.include_desktop_publishing,
      include_proofreading: uploadProject.include_proofreading,
    });
    const serverTotal = Number((uploadProject as any)?.serverTotal || 0) || 0;
    return serverTotal > 0 ? serverTotal : calculated;
  }, [uploadProject]);

  const canSubmit = (uploadProject.documents?.length || 0) > 0 && total > 0;

  useEffect(() => {
    if (user?.user_id) {
      dispatch(getCardsRequest({ user_id: user.user_id }));
    }
  }, [dispatch, user?.user_id]);

  useEffect(() => {
    if (!user?.user_id) return;
    const savedDraftId = localStorage.getItem(getLS_DRAFT_PROJECT_ID(user.user_id));
    if (savedDraftId && !orderId) {
      dispatch(saveDraftProjectId(savedDraftId));
      setCreatedOrderId(savedDraftId);
    }
  }, [dispatch, orderId, user?.user_id]);

  useEffect(() => {
    if (isGettingCards) return;
    if (cards?.length < 1) {
      setOpenCardInputForm(true);
    } else {
      setOpenCardInputForm(false);
    }
  }, [cards, isGettingCards]);

  useEffect(() => {
    if (isGettingCards) return;
    if (openCardInputForm) return;
    if (payment === "paypal") return;
    if (selectedCard) return;

    if (cards?.length > 0) {
      const defaultCard = cards?.find((it: any) => Number(it?.is_default) === 1) || cards?.[0];
      if (defaultCard) {
        setSelectedCard(defaultCard);
        dispatch(updateProjectStatus({ paymentOption: defaultCard }));
        setPayment("stripe");
      }
    }
  }, [cards, dispatch, isGettingCards, openCardInputForm, payment, selectedCard]);

  const initStripeElements = useCallback(async (): Promise<() => void> => {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE;
    if (!key) {
      setError("Stripe publishable key is missing. Please set REACT_APP_STRIPE_PUBLISHABLE.");
      return () => undefined;
    }

    const stripeInstance = await loadStripe(key);
    if (!stripeInstance) {
      setError("Unable to initialize Stripe.");
      return () => undefined;
    }

    setStripe(stripeInstance);

    const elements = stripeInstance.elements();

    const elementStyle = {
      base: {
        iconColor: "#000",
        color: "#000",
        fontWeight: 400,
        fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        fontSize: "16px",
        lineHeight: "30px",
        letterSpacing: "normal",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#9CA3AF",
        },
        ":-webkit-autofill": {
          color: "#777",
        },
      },
      invalid: {
        iconColor: "#F00",
        color: "#F00",
      },
    };

    const cardNumberEl = elements.create("cardNumber", {
      style: elementStyle,
      placeholder: "1234 1234 1234 1234",
    });
    const cardExpiryEl = elements.create("cardExpiry", {
      style: elementStyle,
      placeholder: "06/28",
    });
    const cardCvcEl = elements.create("cardCvc", {
      style: elementStyle,
      placeholder: "369",
    });

    cardNumberEl.mount("#payment-card-number-element");
    cardExpiryEl.mount("#payment-card-expiry-element");
    cardCvcEl.mount("#payment-card-cvc-element");

    let isCardNumberComplete = false;
    let isCardExpiryComplete = false;
    let isCardCvcComplete = false;

    const maybeSetReady = () => {
      if (isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete) {
        setCardNumberElement(cardNumberEl);
      } else {
        setCardNumberElement(null);
      }
    };

    cardNumberEl.on("change", (event: any) => {
      isCardNumberComplete = event.complete;
      if (event.brand) {
        setCardBrand(event.brand);
      }
      maybeSetReady();
    });

    cardExpiryEl.on("change", (event: any) => {
      isCardExpiryComplete = event.complete;
      maybeSetReady();
    });

    cardCvcEl.on("change", (event: any) => {
      isCardCvcComplete = event.complete;
      maybeSetReady();
    });

    return () => {
      try {
        cardNumberEl.unmount();
        cardExpiryEl.unmount();
        cardCvcEl.unmount();
      } catch (e) {
      }
    };
  }, []);

  useEffect(() => {
    if (!openCardInputForm) return;

    dispatch(updateProjectStatus({ paymentOption: null, saveCardAsDefault: false }));
    setSelectedCard(null);
    setPayment("stripe");

    let cleanup: () => void = () => undefined;
    let cancelled = false;

    (async () => {
      try {
        const res = await initStripeElements();
        if (cancelled) {
          res();
          return;
        }
        cleanup = res;
      } catch (e) {
      }
    })();

    return () => {
      cancelled = true;
      cleanup();
      setCardNumberElement(null);
      setStripe(null);
    };
  }, [dispatch, initStripeElements, openCardInputForm]);

  const onRemoveCard = useCallback(
    (cardId: number) => {
      if (!user?.user_id) return;
      dispatch(removeCardInfoRequest({ user_id: user.user_id, card_id: cardId }));
    },
    [dispatch, user?.user_id]
  );

  const onSaveNewCard = useCallback(async () => {
    if (!stripe || !cardNumberElement || _.isEmpty(cardHolderName)) return;
    if (!user?.user_id) return;

    try {
      setIsSavingNewCard(true);
      const result = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: { name: cardHolderName },
      });

      if (result.error) {
        setError(result.error.message || "Unable to save card details. Please review and try again.");
        return;
      }

      const paymentMethodId = result?.paymentMethod?.id;
      if (!paymentMethodId) {
        setError("Unable to save card details. Please review and try again.");
        return;
      }

      await axiosService.post(`/shipping/put/payment-method/${user.user_id}`, {
        payment_method_id: paymentMethodId,
        is_default: 1,
      });

      dispatch(getCardsRequest({ user_id: user.user_id }));
      setOpenCardInputForm(false);
      setCardHolderName("");
      setCardBrand("");
      setError(null);
    } catch (e: any) {
      setError("Unable to save card details. Please review and try again.");
    } finally {
      setIsSavingNewCard(false);
    }
  }, [cardHolderName, cardNumberElement, dispatch, stripe, user?.user_id]);

  const selectPaypal = useCallback(() => {
    setSelectedCard(null);
    setOpenCardInputForm(false);
    setCardHolderName("");
    setCardBrand("");
    setCardNumberElement(null);
    setStripe(null);
    dispatch(updateProjectStatus({ paymentOption: null, saveCardAsDefault: false }));
    setPayment("paypal");
  }, [dispatch]);

  // Prepare order for payment - update existing draft or create new order
  const prepareOrderForPayment = useCallback(async (): Promise<string | null> => {
    // If we already have a draft project, use it
    const existingProjectId = orderId || draftProjectId || createdOrderId;
    
    try {
      setIsCreatingDraft(true);
      const {
        includeExpeditedTurnaround,
        mailedHardCopyRecipientName,
        mailedHardCopyStreetAddress,
        mailedHardCopyCity,
        mailedHardCopyZipCode,
        mailedHardCopyState,
        mailedHardCopyCountry,
        documents,
        serverTotal,
        ...restUploadProject
      } = uploadProject as any;

      const includeExpedited =
        (uploadProject as any)?.include_expedited_turnaround === true ||
        (uploadProject as any)?.include_expedited_turnaround === 1 ||
        (uploadProject as any)?.include_expedited_turnaround === "1";

      const translateSpeed = includeExpedited ? "express" : "standard";

      // Format address: "Recipient; Street address; City; Zip; State; Country"
      const formattedAddress = [
        mailedHardCopyRecipientName || "",
        mailedHardCopyStreetAddress || "",
        mailedHardCopyCity || "",
        mailedHardCopyZipCode || "",
        mailedHardCopyState || "",
        mailedHardCopyCountry || "",
      ].join("; ");

      // Send documents with IDs, counts, file name, fileUrl, and origin_file
      const safeDocuments = (uploadProject.documents || []).map((d: any) => ({
        id: d.id,
        document_id: d.id,
        pages: d.pages || 0,
        words: d.words || 0,
        type: d.type,
        ...(d.file?.name ? { file: { name: d.file.name } } : {}),
        ...(d.fileUrl ? { fileUrl: d.fileUrl } : {}),
        ...(d.origin_file ? { origin_file: d.origin_file } : {}),
      }));

      const payload = {
        ...restUploadProject,
        ...(existingProjectId ? { order_id: existingProjectId } : {}),
        address: formattedAddress,
        documents: safeDocuments,
        document_cnt: uploadProject.documents?.length || 0,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        pageCount: uploadProject.totalPage,
        wordsCount: uploadProject.totalWords,
        price: total,
        include_expedited_turnaround: includeExpedited,
        transitionSpeed: translateSpeed,
        desktopPublishing: !!(uploadProject as any)?.include_desktop_publishing,
        proofreading: !!(uploadProject as any)?.include_proofreading,
        orderStatus: 1, // Ready for payment
        order_due_date: calculateDueDate(uploadProject),
      };

      await waitForPendingDraftSave();
      const resp = await axiosService.post(`/projects/${user.user_id}`, payload);
      const newOrderId = (resp?.data as any)?.orderId || existingProjectId;

      if (newOrderId) {
        localStorage.setItem(getLS_DRAFT_PROJECT_ID(user.user_id), newOrderId);
        dispatch(saveDraftProjectId(newOrderId));
        setCreatedOrderId(newOrderId);
        return newOrderId;
      }
      return null;
    } catch (e: any) {
      console.error("Failed to prepare order for payment:", e);
      setError("Failed to create order. Please try again.");
      return null;
    } finally {
      setIsCreatingDraft(false);
    }
  }, [createdOrderId, dispatch, draftProjectId, orderId, total, uploadProject, user.email, user.first_name, user.last_name, user.user_id]);

  const completeOrder = useCallback(
    async (orderIdToUpdate: string, orderStatus: number) => {
      const {
        includeExpeditedTurnaround,
        mailedHardCopyRecipientName,
        mailedHardCopyStreetAddress,
        mailedHardCopyCity,
        mailedHardCopyZipCode,
        mailedHardCopyState,
        mailedHardCopyCountry,
        documents,
        serverTotal,
        ...restUploadProject
      } = uploadProject as any;

      const includeExpedited =
        (uploadProject as any)?.include_expedited_turnaround === true ||
        (uploadProject as any)?.include_expedited_turnaround === 1 ||
        (uploadProject as any)?.include_expedited_turnaround === "1";

      const translateSpeed = includeExpedited ? "express" : "standard";

      // Format address: "Recipient; Street address; City; Zip; State; Country"
      const formattedAddress = [
        mailedHardCopyRecipientName || "",
        mailedHardCopyStreetAddress || "",
        mailedHardCopyCity || "",
        mailedHardCopyZipCode || "",
        mailedHardCopyState || "",
        mailedHardCopyCountry || "",
      ].join("; ");

      // Send documents with IDs, counts, file name, fileUrl, and origin_file
      const safeDocuments = (uploadProject.documents || []).map((d: any) => ({
        id: d.id,
        document_id: d.id,
        pages: d.pages || 0,
        words: d.words || 0,
        type: d.type,
        ...(d.file?.name ? { file: { name: d.file.name } } : {}),
        ...(d.fileUrl ? { fileUrl: d.fileUrl } : {}),
        ...(d.origin_file ? { origin_file: d.origin_file } : {}),
      }));

      const payload = {
        ...restUploadProject,
        address: formattedAddress,
        order_id: orderIdToUpdate,
        documents: safeDocuments,
        document_cnt: uploadProject.documents?.length || 0,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        pageCount: uploadProject.totalPage,
        wordsCount: uploadProject.totalWords,
        price: total,
        include_expedited_turnaround: includeExpedited,
        transitionSpeed: translateSpeed,
        desktopPublishing: !!(uploadProject as any)?.include_desktop_publishing,
        proofreading: !!(uploadProject as any)?.include_proofreading,
        orderStatus: orderStatus,
        order_due_date: calculateDueDate(uploadProject),
      };

      await waitForPendingDraftSave();
      await axiosService.post(`/projects/${user.user_id}`, payload);
    },
    [total, uploadProject, user.email, user.first_name, user.last_name, user.user_id]
  );

  // The charge has already succeeded when this runs, so a single failed save
  // must not leave the order stuck as Draft/Pending. Retry before giving up.
  const finalizePaidOrder = useCallback(
    async (orderIdToUpdate: string): Promise<boolean> => {
      for (let attempt = 1; attempt <= FINALIZE_PAID_ORDER_ATTEMPTS; attempt++) {
        try {
          await completeOrder(orderIdToUpdate, 2);
          return true;
        } catch (e: any) {
          console.error(`Failed to finalize paid order (attempt ${attempt}):`, e);
          if (attempt < FINALIZE_PAID_ORDER_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, FINALIZE_PAID_ORDER_RETRY_MS));
          }
        }
      }
      return false;
    },
    [completeOrder]
  );

  const clearWizardDraft = useCallback(() => {
    if (!user?.user_id) return;
    try {
      localStorage.removeItem(getLS_DRAFT(user.user_id));
      localStorage.removeItem(getLS_STEP(user.user_id));
      localStorage.removeItem(getLS_MAX_STEP(user.user_id));
      localStorage.removeItem(getLS_DRAFT_PROJECT_ID(user.user_id));
    } catch (e) {
    }
  }, [user?.user_id]);

  const onProceedStripePayment = useCallback(
    async (tokenId: string | null) => {
      lockDraftSaves();

      // Always persist the full order (documents, counts, price) before
      // charging, even when a draft id already exists. The Stripe amount comes
      // from local state, so charging an order the server failed to save
      // leaves a paid order showing Draft / $0 / no files.
      const paymentOrderId = await prepareOrderForPayment();
      if (!paymentOrderId) {
        unlockDraftSaves();
        setError(ORDER_NOT_SAVED_MESSAGE);
        return;
      }

      const onPaymentSucceeded = async () => {
        const finalized = await finalizePaidOrder(paymentOrderId);
        if (!finalized) {
          setError(paidButNotFinalizedMessage(paymentOrderId));
          return;
        }
        if (user?.user_id) {
          localStorage.removeItem(getLS_DRAFT_PROJECT_ID(user.user_id));
        }
        dispatch(clearDraftProjectId());
        clearWizardDraft();
        setSuccess("Payment successful! Redirecting...");
        setTimeout(() => navigate(`/customer/order/completed/${paymentOrderId}`, { state: { total } }), 800);
      };

      const onPaymentNotCompleted = async () => {
        try {
          await completeOrder(paymentOrderId, 1);
        } catch (e: any) {
          console.error("Failed to record incomplete payment:", e);
        }
        unlockDraftSaves();
        setError("Payment is not completed. Please try again or contact support.");
      };

      if (tokenId) {
        const paymentPayload = {
          amount: total,
          token: tokenId,
          isRedeem: isRedeemEnabled,
        };
        const res: any = await axiosService.post(`/shipping/process-token-payment/${paymentOrderId}`, paymentPayload);
        if (res?.data?.payment_status === "succeeded") {
          await onSaveNewCard();
          await onPaymentSucceeded();
        } else {
          await onPaymentNotCompleted();
        }
        return;
      }

      if (selectedCard?.pm_id) {
        const res: any = await axiosService.post(`/shipping/process-pm-payment/${paymentOrderId}`, {
          amount: total,
          pm_id: selectedCard?.pm_id,
          customer_id: selectedCard?.customer_id,
          isRedeem: isRedeemEnabled,
        });

        if (res?.data?.payment_status === "succeeded") {
          await onPaymentSucceeded();
        } else {
          await onPaymentNotCompleted();
        }
        return;
      }

      unlockDraftSaves();
    },
    [clearWizardDraft, completeOrder, finalizePaidOrder, prepareOrderForPayment, dispatch, isRedeemEnabled, navigate, onSaveNewCard, selectedCard?.customer_id, selectedCard?.pm_id, total, user?.user_id]
  );

  const onPayNow = useCallback(async () => {
    setError(null);
    setSuccess(null);

    if (payment === "paypal") {
      // Same rule as card payments: the order must be saved server-side
      // before any money moves.
      try {
        setIsSubmitting(true);
        const paymentOrderId = await prepareOrderForPayment();
        if (!paymentOrderId) {
          setError(ORDER_NOT_SAVED_MESSAGE);
          return;
        }
      } finally {
        setIsSubmitting(false);
      }
      paypalRef.current?.open();
      return;
    }

    try {
      setIsSubmitting(true);

      if (selectedCard) {
        await onProceedStripePayment(null);
        return;
      }

      if (!stripe || !cardNumberElement || _.isEmpty(cardHolderName)) {
        setError("Please enter valid card details.");
        return;
      }

      const res = await stripe.createToken(cardNumberElement as any, { name: cardHolderName });
      const tokenId = (res as any)?.token?.id;
      if (!tokenId) {
        setError((res as any)?.error?.message || "Error in generating token. Please check the card information.");
        return;
      }
      await onProceedStripePayment(tokenId);
    } catch (e: any) {
      unlockDraftSaves();
      setError("Payment failed. Please check your card details or try another method.");
    } finally {
      setIsSubmitting(false);
    }
  }, [cardHolderName, cardNumberElement, onProceedStripePayment, payment, prepareOrderForPayment, selectedCard, stripe]);

  const selectStripe = useCallback(() => {
    setPayment("stripe");
  }, []);

  const onSelectCard = useCallback(
    (card: any) => {
      setSelectedCard(card);
      setOpenCardInputForm(false);
      setPayment("stripe");
      dispatch(updateProjectStatus({ paymentOption: card }));
    },
    [dispatch]
  );

  const onToggleAddNewCard = useCallback(() => {
    setSelectedCard(null);
    setCardHolderName("");
    setCardBrand("");
    setOpenCardInputForm((v) => !v);
  }, []);

  const isPrimaryDisabled =
    !canSubmit ||
    isCreatingDraft ||
    !(selectedCard || (stripe && cardNumberElement && !_.isEmpty(cardHolderName)) || payment === "paypal");

  return {
    orderId,
    uploadProject,
    user,
    cards,
    isGettingCards,

    total,
    canSubmit,
    isPrimaryDisabled,

    error,
    success,
    isSubmitting,

    payment,
    openCardInputForm,
    selectedCard,

    stripe,
    cardNumberElement,
    cardHolderName,
    cardBrand,
    isSavingNewCard,

    paypalRef,

    setCardHolderName,

    selectStripe,
    selectPaypal,
    onSelectCard,
    onToggleAddNewCard,
    onRemoveCard,
    onSaveNewCard,
    onPayNow,

    onPaypalCompleted: async (orderStatus: number) => {
      // PayPal has already captured (or declined) by the time this runs.
      lockDraftSaves();

      let paymentOrderId = orderId || draftProjectId || createdOrderId;

      if (!paymentOrderId) {
        paymentOrderId = await prepareOrderForPayment();
        if (!paymentOrderId) {
          if (orderStatus === 2) {
            setError(paidButNotFinalizedMessage());
          } else {
            unlockDraftSaves();
            setError(ORDER_NOT_SAVED_MESSAGE);
          }
          return;
        }
      }

      if (orderStatus === 2) {
        const finalized = await finalizePaidOrder(paymentOrderId);
        if (!finalized) {
          setError(paidButNotFinalizedMessage(paymentOrderId));
          return;
        }
        if (user?.user_id) {
          localStorage.removeItem(getLS_DRAFT_PROJECT_ID(user.user_id));
        }
        dispatch(clearDraftProjectId());
        clearWizardDraft();
        setSuccess("Payment successful! Redirecting...");
        setTimeout(() => navigate(`/customer/order/completed/${paymentOrderId}`, { state: { total } }), 800);
      } else {
        try {
          await completeOrder(paymentOrderId, orderStatus);
        } catch (e: any) {
          console.error("Failed to record incomplete PayPal payment:", e);
        }
        unlockDraftSaves();
        setError("Payment is not completed. Please try again or contact support.");
      }
    },
  };
}
